import { getAccessToken, isTokenExpired, clearAuth } from './tokens';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name: string, value: string, maxAgeSec?: number): void {
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Strict`;
  if (maxAgeSec !== undefined) {
    cookie += `; max-age=${maxAgeSec}`;
  }
  if (process.env.NODE_ENV === 'production') {
    cookie += '; Secure';
  }
  document.cookie = cookie;
}

interface SessionConfig {
  refreshThresholdSeconds: number;
  onTokenExpired: () => void;
  onRefreshFailed: () => void;
}

const DEFAULT_CONFIG: SessionConfig = {
  refreshThresholdSeconds: 60,
  onTokenExpired: () => {
    clearAuth();
    window.location.href = '/auth/login';
  },
  onRefreshFailed: () => {
    clearAuth();
    window.location.href = '/auth/login';
  },
};

let sessionConfig = DEFAULT_CONFIG;
let refreshPromise: Promise<string> | null = null;

export function configureSession(config: Partial<SessionConfig>): void {
  sessionConfig = { ...DEFAULT_CONFIG, ...config };
}

export async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const refreshToken = getCookie('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      const accessToken = data.accessToken;
      const newRefreshToken = data.refreshToken;
      const expiresIn = data.expiresIn ?? 900;

      setCookie('access_token', accessToken, expiresIn);
      if (newRefreshToken) {
        setCookie('refresh_token', newRefreshToken, 604800);
      }
      return accessToken;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function getValidToken(): Promise<string | null> {
  const token = getAccessToken();

  if (!token) {
    return null;
  }

  if (!isTokenExpired()) {
    return token;
  }

  try {
    return await refreshAccessToken();
  } catch {
    sessionConfig.onRefreshFailed();
    return null;
  }
}

export function isAuthenticated(): boolean {
  const token = getAccessToken();
  return !!token && !isTokenExpired();
}

export function logout(): void {
  clearAuth();
  window.location.href = '/auth/login';
}
